import { computed, ComputedRef, ref, Ref } from "vue";
import { uniqBy } from "lodash-es";
import axios, { AxiosResponse } from "axios";
import { NotificationItem, useNotificationStore } from "@/stores/notification";

interface NotificationResponse {
  data: NotificationItem[];
  total: number;
}

interface NotificationCheckResponse {
  count: number;
}

export default function useNotification() {
  const notification = useNotificationStore();

  const unreadCount: ComputedRef<number> = computed(() => notification.unreadCount);
  const items: ComputedRef<NotificationItem[]> = computed(() => notification.items);
  const currentPage: ComputedRef<number> = computed(() => notification.page);

  // UI states for loading and errors
  const isLoading: Ref<boolean> = ref(false);

  // Helper functions for Vuex actions
  const setUnreadCount = (value: number): void => notification.setUnreadCount(value);
  const setPage = (value: number): void => notification.setPage(value);
  const setItems = (value: NotificationItem[]): void => notification.setItems(value);
  const updateItem = (item: NotificationItem): void => notification.setItems([item]);
  const push = (item: NotificationItem): void => notification.pushItem(item);
  const unshift = (item: NotificationItem): void => notification.unshiftItem(item);

  // Reset notifications
  const reset = (): void => notification.reset();

  // Check notifications count and update count
  const check = (): Promise<void> =>
    axios.get<NotificationCheckResponse>(route("ajax.notifications.count")).then(({ data }) => setUnreadCount(Number(data.count)));

  const clear = async (): Promise<void> => {
    await notification.reset();
    await axios.delete(route("ajax.notifications.clear-all"));
  };

  const read = async (item: NotificationItem): Promise<void> => {
    if (!item.read_at) {
      await notification.read(item);
      await axios.put(route("ajax.notifications.mark-as-read", item));
    }
  };

  const readAll = async (): Promise<void> => {
    if (unreadCount.value > 0) {
      await notification.readAll();
      await axios.put(route("ajax.notifications.mark-all-as-read"));
      setUnreadCount(0);
    }
  };

  // Fetch notifications
  const fetch = async (): Promise<NotificationResponse | null> => {
    isLoading.value = true;
    let data: NotificationResponse | null = null;
    try {
      const response: AxiosResponse<NotificationResponse> = await axios.get(route("ajax.notifications.index"), {
        params: { page: currentPage.value },
      });
      data = response.data;

      if (data.data.length > 0) {
        setItems(uniqBy([...items.value, ...data.data], "id"));
        setPage(currentPage.value + 1);
      }
    } finally {
      isLoading.value = false;
    }

    return data;
  };

  return {
    unreadCount,
    items,
    isLoading,
    read,
    readAll,
    check,
    fetch,
    reset,
    setPage,
    setUnreadCount,
    setItems,
    updateItem,
    push,
    unshift,
    clear,
  };
}

declare function route(name: string, params?: any): string;
